import type { ClosureReason, OrganizationClosurePreview, OrganizationStatus } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
  Modal,
  Select,
  Textarea,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, ApiRequestError } from "../../lib/apiClient";
import { tenantKey } from "../../lib/tenantQuery";

/**
 * Organizasyon kapatma / yeniden etkinleştirme yüzeyi (CLOS-195 `[054]`).
 *
 * ### ⚠ Etki KARARDAN ÖNCE
 *
 * Ön izleme ayrı bir uçtan ve pencere açılırken çekiliyor. Kapatma yanıtında dönseydi
 * yönetici neyi durdurduğunu ancak durdurduktan sonra öğrenirdi (`[041]` ayrılma ön
 * izlemesi ve `[052]` departman etki özetiyle aynı gerekçe).
 *
 * ### ⚠ Kapatmanın YAPMADIKLARI da söyleniyor
 *
 * Metin SUNUCUDAN (`preserved`). Kart: *"kapatma tek başına aboneliği iptal etmez, ödeme
 * iadesi oluşturmaz veya saklama politikasını değiştirmez."* Söylenmeseydi yönetici
 * organizasyonu kapatıp faturanın da durduğunu sanar, bir sonraki tahsilatta şaşırırdı.
 *
 * ### ⚠ Yeniden etkinleştirme yetki geri getirmiyor — ve bunu SÖYLÜYOR
 *
 * Sessizce yapsaydık yönetici eski ekibin döndüğünü sanır, kimseyi yeniden
 * yetkilendirmezdi.
 */

type KapatmaOnizleme = OrganizationClosurePreview & {
  consequences: string[];
  reasons: ClosureReason[];
  /** Parola SORULACAK MI — politika sunucuda (`config/organization.json`). */
  requiresPassword: boolean;
};

export function OrganizationLifecycleCard({
  tenantId,
  status,
}: {
  tenantId: string;
  status: OrganizationStatus;
}) {
  const [acik, setAcik] = useState<"close" | "reactivate" | null>(null);

  return (
    <Card title="Organizasyon durumu">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-fg-muted">Operasyon durumu:</span>
          <Badge tone={status === "active" ? "success" : "danger"}>
            {status === "active" ? "Aktif" : "Kapalı"}
          </Badge>
        </div>
        {status === "active" ? (
          <Button variant="ghost" size="sm" onClick={() => setAcik("close")}>
            Organizasyonu kapat
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setAcik("reactivate")}>
            Yeniden etkinleştir
          </Button>
        )}
      </div>

      {acik === "close" ? <CloseDialog tenantId={tenantId} onClose={() => setAcik(null)} /> : null}
      {acik === "reactivate" ? (
        <ReactivateDialog tenantId={tenantId} onClose={() => setAcik(null)} />
      ) : null}
    </Card>
  );
}

function CloseDialog({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [gerekce, setGerekce] = useState("");
  const [not, setNot] = useState("");
  const [parola, setParola] = useState("");

  const onizleme = useQuery({
    queryKey: tenantKey(tenantId, "closure"),
    queryFn: async () =>
      (await api<KapatmaOnizleme>(`/api/tenants/${tenantId}/closure`, { tenantId })).data,
  });

  const komut = useMutation({
    mutationFn: async () => {
      await api(`/api/tenants/${tenantId}/close`, {
        method: "POST",
        body: JSON.stringify({
          reason: gerekce || onizleme.data?.reasons[0]?.code,
          ...(not ? { note: not } : {}),
          confirm: true,
          ...(onizleme.data?.requiresPassword ? { currentPassword: parola } : {}),
        }),
        tenantId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "organization-profile"),
      });
      toast.success("Organizasyon kapatıldı");
      onClose();
    },
    /** Sunucunun cümlesi: "mevcut şifreniz doğrulanamadı" gibi bir ret kendi nedenini taşır. */
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  const ozet = onizleme.data;

  return (
    <Modal
      open
      onClose={onClose}
      title="Organizasyonu kapat"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          {/*
            Onay düğmesi tetikleyiciyle AYNI metni taşımıyor: iki "Organizasyonu kapat"
            düğmesi ekran okuyucuda ayırt edilemezdi (test "Found multiple" ile yakaladı).
            Metin yine EYLEMİ adlandırıyor — çıplak bir "Onayla", geri alınamaz bir işlemde
            neyin onaylandığını söylemezdi.
          */}
          <Button loading={komut.isPending} onClick={() => komut.mutate()}>
            Kapatmayı onayla
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {ozet ? (
          <>
            <div className="text-sm text-fg">
              <p className="font-medium">Bu işlem şunları etkiler:</p>
              <ul className="mt-1 list-disc pl-5 text-fg-muted">
                <li>{ozet.activeMemberships} aktif üyelik</li>
                <li>{ozet.pendingInvitations} bekleyen davet</li>
                <li>{ozet.activeDepartments} aktif departman</li>
                <li>{ozet.customRoles} özel rol</li>
              </ul>
            </div>

            <ul className="list-disc pl-5 text-sm text-fg-muted">
              {ozet.consequences.map((satir) => (
                <li key={satir}>{satir}</li>
              ))}
            </ul>

            {/* Kapatmanın YAPMADIKLARI — metin sunucudan, sayfa kendi cümlesini yazmıyor. */}
            <div className="rounded-md bg-muted p-3 text-sm text-fg-muted">
              <p className="font-medium text-fg-strong">Bu işlem şunları DEĞİŞTİRMEZ:</p>
              <ul className="mt-1 list-disc pl-5">
                {ozet.preserved.map((satir) => (
                  <li key={satir}>{satir}</li>
                ))}
              </ul>
            </div>

            <FormField label="Gerekçe" required>
              <Select
                value={gerekce || (ozet.reasons[0]?.code ?? "")}
                onChange={(event) => setGerekce(event.target.value)}
              >
                {ozet.reasons.map((sebep) => (
                  <option key={sebep.code} value={sebep.code}>
                    {sebep.label}
                  </option>
                ))}
              </Select>
            </FormField>

            <FormField label="Not" hint="İsteğe bağlı; denetim kaydına yazılır">
              <Textarea rows={2} value={not} onChange={(event) => setNot(event.target.value)} />
            </FormField>

            {/* Parola SORULACAK MI kararı politikadan; sayfa kendi kuralını üretmiyor. */}
            {ozet.requiresPassword ? (
              <FormField label="Mevcut şifreniz" required>
                <Input
                  type="password"
                  value={parola}
                  onChange={(event) => setParola(event.target.value)}
                />
              </FormField>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}

function ReactivateDialog({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const komut = useMutation({
    mutationFn: async () => {
      await api(`/api/tenants/${tenantId}/reactivate`, {
        method: "POST",
        body: JSON.stringify({ confirm: true }),
        tenantId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: tenantKey(tenantId, "organization-profile"),
      });
      toast.success("Organizasyon yeniden etkinleştirildi");
      onClose();
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError ? error.message : "İşlem tamamlanamadı, tekrar deneyin.",
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Organizasyonu yeniden etkinleştir"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button loading={komut.isPending} onClick={() => komut.mutate()}>
            Onayla
          </Button>
        </div>
      }
    >
      {/*
        ⚠ Kartın no-auto-restore kuralı KULLANICIYA söyleniyor. Sessizce yapsaydık yönetici
        eski ekibin döndüğünü sanır, kimseyi yeniden yetkilendirmezdi.
      */}
      <p className="text-sm text-fg-muted">
        Organizasyon yeniden çalışmaya başlar; eski yetkiler, roller ve erişimler geri gelmez. Sona
        ermiş üyelikler, arşivlenmiş roller ve iptal edilmiş davetler olduğu gibi kalır.
      </p>
    </Modal>
  );
}
